import React, { useContext, useCallback, useRef, useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';
import { PageElementsContext } from '../../../context/pageElementsContext';
import { RadioButtonSection } from '../../../artboard/Page/styles';
import FillableRadioButtonSettings from '../../../components/GroupSettings/IndividualBoxSettings';
import {
  OPTIONS_KEY,
  FIELD_NAME_KEY,
  IS_VARIABLE_KEY,
  GROUP_NAME_KEY,
  TEXT_DEBOUNCE_TIME_OUT,
  READ_ONLY_KEY,
  IS_CHECKED_KEY,
  FILLABLE_RADIO_BUTTON_TYPE,
  dummyCallback,
} from '../../../utils/complexDocumentConstants';
import { deepCopy, random4Characters } from '../../../utils/javascript';
import { v4 } from 'uuid';
import { Tooltip } from 'antd';
import { ColorButton } from '../../../artboard/Toolbar/styles';
import { ELEMENTS_SETTINGS_TOOLBAR } from '../../../artboard/Toolbar/translationValues';
import ColorPicker from '../../../artboard/ColorPicker';
import { useTranslation } from 'react-i18next';
import { radioButtonTextElement } from '../../../utils/masterElements/fillableInputs';
import { useDocumentContext } from '../../../context/complexDocumentContext';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { FillableElementWrapper } from '../styles';

const settingsGetter = el => el[OPTIONS_KEY];
const getElementDetails = el => el;
const allStyleKeys = ['borderColor', "backgroundColor", "color"];
const [borderColorKey, ...styleKeys] = allStyleKeys;
const NAMESPACES = ['ELEMENTS_SETTINGS_TOOLBAR', 'FILLABLE_ELEMENTS_MAIN_CONTENT'];

const ReusableRadioButton = props => {
  const {
    element,
    editable,
    selected,
    refreshSelectedElementIds = dummyCallback,
    staticTableBlock,
    parentUUID,
    parentStoredElements,
    updateParentComplexElementDetails,
    onCellDoubleClick,
    isInteractiveDesign
  } = props;
  const { updateComplexElementDetails, handleStoredElements, storedElements } = useContext(
    PageElementsContext,
  );
  const [picker, setPicker] = useState(false);
  const [, methods] = useDocumentContext(() => null);
  const { t } = useTranslation(NAMESPACES);

  const [, updateArtboard] = useArtboardContext(() => null);
  useEffect(() => {
    // close the sidebar when the element is removed from the block.
    return () => {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    };
  }, []);

  const memoizedParentElements = useMemo(() => {
    if (staticTableBlock && parentStoredElements?.length) {
      const currentStore = methods.get();
      return parentStoredElements[0].contents.map(cont => currentStore[cont.uuid]);
    }
    return [];
  }, [staticTableBlock, parentStoredElements, methods]);

  const memoizedOtherRadiobuttons = useMemo(() => {
    if (memoizedParentElements?.length) {
      return memoizedParentElements.filter(
        el => el && el?.fillableType === FILLABLE_RADIO_BUTTON_TYPE && el?.uuid !== element.uuid,
      );
    }
    return storedElements.filter(
      el => el?.fillableType === FILLABLE_RADIO_BUTTON_TYPE && el?.uuid !== element.uuid,
    );
  }, [storedElements, element, memoizedParentElements]);

  const updateStaticTableRadiobutton = useCallback(
    details => {
      details.flowUUID = parentUUID;
      methods.updateBlock('static-table-cells-form-options', element.parentBlockUUID, details);
    },
    [element.parentBlockUUID, parentUUID],
  );

  const updateElementDetails = useCallback(
    (value, key) => {
      const details = {};
      if ([IS_VARIABLE_KEY, READ_ONLY_KEY, IS_CHECKED_KEY, ...allStyleKeys].includes(key)) {
        const sameGroupElsUuids = (staticTableBlock ? memoizedParentElements : storedElements)
          .filter(
            el =>
              el?.formOptions?.groupName === element.formOptions.groupName &&
              el?.uuid !== element.uuid,
          )
          .map(el => el.uuid);
        details.extraFormElementsUuids = sameGroupElsUuids;
      }
      if (key === IS_VARIABLE_KEY) {
        details.style = {
          [IS_VARIABLE_KEY]: value,
        };
      } else if (styleKeys.includes(key)) {
        details.style = { [key]: value };
      } else if (key === borderColorKey)  {
        details.style = { border: { [key]: value } };
      } else {
        details.options = { [key]: value };
        if (key === IS_CHECKED_KEY) {
          details.negativeOptions = { [IS_CHECKED_KEY]: false };
        }
      }
      if (staticTableBlock) {
        details.uuid = element.uuid;
        updateStaticTableRadiobutton(details);
      } else {
        updateComplexElementDetails(element.uuid, details, 'form-options');
      }
    },
    [updateComplexElementDetails, element, storedElements, staticTableBlock, memoizedParentElements, updateStaticTableRadiobutton],
  );

  const debouncedUpdateComplexElementDetails = useRef(
    _.debounce(updateComplexElementDetails, TEXT_DEBOUNCE_TIME_OUT),
  ).current;

  const onFieldNameChange = useCallback(value => {
    console.log({ value });
    // TODO : Check for duplicates
    debouncedUpdateComplexElementDetails(
      element.uuid,
      { options: { [FIELD_NAME_KEY]: value } },
      'form-options',
    );
  }, []);

  const onGroupNameChange = useCallback(value => {
    debouncedUpdateComplexElementDetails(
      element.uuid,
      { options: { [GROUP_NAME_KEY]: value } },
      'form-options',
    );
  }, []);

  const onConfirmGroupNameChange = value => {
    const isGroupElAlreadyAvailaibale = memoizedOtherRadiobuttons.find(
      el => el.formOptions.groupName === value,
    );
    if (isGroupElAlreadyAvailaibale) {
      const { groupName, isReadOnly } = isGroupElAlreadyAvailaibale.formOptions;
      const { color, border, backgroundColor, isVariable } = isGroupElAlreadyAvailaibale.style;
      const details = {
        options: { isChecked: false, groupName, isReadOnly },
        style: { color, border, backgroundColor, isVariable },
      };
      updateComplexElementDetails(element.uuid, details, 'form-options');
    }
  };

  const onOtherNameChange = useCallback(
    (value, el) => {
      if (staticTableBlock) {
        updateStaticTableRadiobutton({
          uuid: element.uuid,
          options: { [FIELD_NAME_KEY]: value },
        });
      } else {
        debouncedUpdateComplexElementDetails(
          el.uuid,
          { options: { [FIELD_NAME_KEY]: value } },
          'form-options',
        );
      }
    },
    [
      staticTableBlock,
      element.uuid,
      debouncedUpdateComplexElementDetails,
      updateStaticTableRadiobutton,
    ],
  );

  const showBackgroundColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'backgroundColor') {
        return false;
      }
      return { type: 'backgroundColor', color: element.style.backgroundColor };
    });
  }, [element]);

  const showBorderColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'borderColor') {
        return false;
      }
      return { type: 'borderColor', color: element.style.border?.borderColor };
    });
  }, [element]);

  const showElementColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'color') {
        return false;
      }
      return { type: 'color', color: element.style.border?.borderColor };
    });
  }, [element]);

  const onColorPick = useCallback(
    color => {
      if (picker.type === 'backgroundColor') {
        updateElementDetails(color.hex, 'backgroundColor');
        setPicker({ type: 'backgroundColor', color: color.hex });
      } else if (picker.type === 'borderColor') {
        updateElementDetails(color.hex, 'borderColor');
        setPicker({ type: 'borderColor', color: color.hex });
      } else if (picker.type === 'color') {
        updateElementDetails(color.hex, 'color');
        setPicker({ type: 'color', color: color.hex });
      }
    },
    [picker],
  );

  const addNewRadiobutton = () => {
    const copyEl = deepCopy(element);
    copyEl.uuid = v4();
    copyEl.formOptions.fieldName = `radio-button-${random4Characters()}`;
    copyEl.formOptions.isChecked = false;
    if (staticTableBlock) {
      const currentElement = parentStoredElements[0].contents.find(el => el.uuid === element.uuid);
      updateParentComplexElementDetails(
        element.parentBlockUUID,
        {
          index: currentElement.row - 1,
          isTop: false,
          isBlock: true,
          newElement: copyEl,
          colIndex: currentElement.column,
          formUUID: copyEl.uuid
        },
        'static-table-add-row',
      );
      requestAnimationFrame(() =>
        onCellDoubleClick({ target: document.getElementById(copyEl.uuid), persist: dummyCallback }),
      );
    } else {
      copyEl.style.y = copyEl.style.y + 30;
      const additionalTextEl = radioButtonTextElement(t);
      additionalTextEl.uuid = v4();
      additionalTextEl.style.x = copyEl.style.x + additionalTextEl.style.x;
      additionalTextEl.style.y = copyEl.style.y + additionalTextEl.style.y;
      handleStoredElements([copyEl, additionalTextEl], true);
      refreshSelectedElementIds([copyEl.uuid]);
    }
  };

  const selectOtherCheckbox = euUuid => {
    if (staticTableBlock) {
      onCellDoubleClick({ target: document.getElementById(euUuid), persist: dummyCallback });
    } else {
      refreshSelectedElementIds([euUuid]);
    }
  };

  const checked = element?.formOptions?.isChecked;
  // check if parent width is greater than child width, then based on that set the height of the container to 100%
  // so the radiobutton overlaps when container becomes smaller than the radiobutton
  const parentElementStyle = document.getElementById(element.uuid);
  const parentWidth = parentElementStyle?.offsetWidth || parentElementStyle?.clientWidth;
  const childWidthGreaterThanParent = element?.style?.width > parentWidth;

  return (
    <>
      <RadioButtonSection
        borderColor={element.style?.border?.borderColor}
        backgroundColor={element.style?.backgroundColor}
        color={element.style?.color}
        staticTableBlock={staticTableBlock}
        childWidthGreaterThanParent={childWidthGreaterThanParent}
        style={{ pointerEvents: !editable && 'none' }}
        className={`complex-documents`}
      >
        <label
          className={`ant-radio-wrapper ${editable ? '' : 'ant-radio-wrapper-disabled'} ${
            checked ? 'ant-radio-wrapper-checked' : ''
          }`}
        >
          <span
            className={`ant-radio ${editable ? '' : 'ant-radio-disabled'} ${
              checked ? 'ant-radio-checked' : ''
            }`}
          >
            <input
              type="radio"
              className="ant-radio-input"
              id={element[OPTIONS_KEY][FIELD_NAME_KEY]}
              name={element[OPTIONS_KEY][GROUP_NAME_KEY]}
              checked={element[OPTIONS_KEY][IS_CHECKED_KEY]}
            />
            <span className="ant-radio-inner"></span>
          </span>
        </label>
      </RadioButtonSection>
      {editable &&
        selected &&
        ReactDOM.createPortal(
          <FillableRadioButtonSettings
            element={element}
            isRadio={true}
            updateElementDetails={updateElementDetails}
            onFieldNameChange={onFieldNameChange}
            onGroupNameChange={onGroupNameChange}
            settingsGetter={settingsGetter}
            OPTIONS_KEY={OPTIONS_KEY}
            FIELD_NAME_KEY={FIELD_NAME_KEY}
            READ_ONLY_KEY={READ_ONLY_KEY}
            GROUP_NAME_KEY={GROUP_NAME_KEY}
            getElementDetails={getElementDetails}
            addNewElement={addNewRadiobutton}
            memoizedOtherFormElements={memoizedOtherRadiobuttons}
            onOtherNameChange={onOtherNameChange}
            onConfirmGroupNameChange={onConfirmGroupNameChange}
            refreshSelectedElementIds={selectOtherCheckbox}
            isInteractiveDesign={isInteractiveDesign}
          />,
          document.getElementById('sidebar-absolute-container'),
        )}
      {editable &&
        selected &&
        ReactDOM.createPortal(
          <FillableElementWrapper>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{ backgroundColor: element.style.backgroundColor, position: 'relative' }}
                onClick={showBackgroundColorPicker}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{ backgroundColor: element.style.border?.borderColor, position: 'relative' }}
                onClick={showBorderColorPicker}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_COLOR_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_COLOR_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{ backgroundColor: element.style.color, position: 'relative' }}
                onClick={showElementColorPicker}
              />
            </Tooltip>
            {picker && (
              <div style={{ position: 'absolute', top: '100%' }}>
                <ColorPicker
                  onChange={onColorPick}
                  onBlur={() => setPicker(false)}
                  color={picker.color}
                  qlEditor={true}
                />
              </div>
            )}
          </FillableElementWrapper>,
          document.getElementById('document-editor-toolbar'),
        )}
    </>
  );
};

export default ReusableRadioButton;
